import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupsIcon from '@mui/icons-material/Groups';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Box,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import React from 'react';
import { Button, Container, Heading, Text } from '../components/atomic';
import { ContentPageLayout } from '../layouts';

/**
 * Join as a Consultant page component for RSM Collective
 *
 * Page for professional consultants interested in joining RSM Collective's curated
 * collection of services and solutions exclusively transacted on AWS Marketplace
 *
 * @returns {JSX.Element} Join as a Consultant page component
 */
function JoinConsultant() {
  const benefits = [
    {
      icon: <GroupsIcon />,
      title: 'AWS Marketplace',
      description:
        'Join our curated collection of professional services on AWS Marketplace',
    },
    {
      icon: <AttachMoneyIcon />,
      title: 'Streamlined Transactions',
      description:
        'All services and solutions exclusively transacted through AWS Marketplace',
    },
    {
      icon: <SchoolIcon />,
      title: 'Professional Excellence',
      description:
        'Deliver high-quality consulting services to enterprise clients',
    },
    {
      icon: <TrendingUpIcon />,
      title: 'Selective Partnership',
      description:
        'We consider exceptional talent when the right fit aligns with our mission',
    },
  ];

  const serviceAreas = [
    'FinOps and cloud cost optimization',
    'GenAI governance and implementation',
    'Cloud migration and modernization',
    'Digital transformation consulting',
    'Technology strategy and planning',
    'Enterprise software implementations',
    'Process optimization and automation',
    'Data analytics and business intelligence',
  ];

  const processSteps = [
    {
      step: '1',
      title: 'Conversation',
      description:
        'Start with a discussion about your expertise and our opportunities',
    },
    {
      step: '2',
      title: 'Assessment',
      description:
        'Evaluate alignment with our service offerings and marketplace model',
    },
    {
      step: '3',
      title: 'Partnership',
      description:
        'Explore potential collaboration within our curated collection',
    },
    {
      step: '4',
      title: 'AWS Integration',
      description:
        'Join our professional services available on AWS Marketplace',
    },
  ];

  return (
    <ContentPageLayout
      useHeader={true}
      title="Join Our Professional Services"
      subtitle=""
      headerDark={false}
      backgroundColor="var(--color-background-light)"
      maxWidth="lg"
    >
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Introduction */}
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <Heading
            variant="h2"
            sx={{ mb: 3, fontSize: '2rem', fontWeight: 600 }}
          >
            Join Our Curated Professional Services Collection
          </Heading>
          <Text
            sx={{
              fontSize: '1.125rem',
              lineHeight: 1.7,
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            RSM Collective offers a curated collection of professional services
            and solutions exclusively transacted on AWS Marketplace. We aren't
            actively hiring, but if the right fit aligns with our vision, we'll
            consider exceptional talent. The first step is always a
            conversation.
          </Text>
        </Box>

        {/* Benefits Section */}
        <Box sx={{ mb: 8 }}>
          <Heading
            variant="h2"
            sx={{ mb: 4, fontSize: '1.75rem', fontWeight: 600 }}
          >
            Our Professional Services Model
          </Heading>
          <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={6} lg={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    maxWidth: '300px',
                    mx: 'auto',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                    position: 'relative',
                    border: '1px solid var(--color-border)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -1,
                      left: index % 2 === 0 ? -1 : 'auto',
                      right: index % 2 === 1 ? -1 : 'auto',
                      width: '40px',
                      height: '40px',
                      borderTop: '3px solid var(--color-primary)',
                      borderLeft:
                        index % 2 === 0
                          ? '3px solid var(--color-primary)'
                          : 'none',
                      borderRight:
                        index % 2 === 1
                          ? '3px solid var(--color-primary)'
                          : 'none',
                      borderRadius: '0 !important',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -1,
                      right: index % 2 === 0 ? -1 : 'auto',
                      left: index % 2 === 1 ? -1 : 'auto',
                      width: '40px',
                      height: '40px',
                      borderBottom: '3px solid var(--color-primary)',
                      borderRight:
                        index % 2 === 0
                          ? '3px solid var(--color-primary)'
                          : 'none',
                      borderLeft:
                        index % 2 === 1
                          ? '3px solid var(--color-primary)'
                          : 'none',
                      borderRadius: '0 !important',
                    },
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ color: 'primary.main', mb: 2 }}>
                      {React.cloneElement(benefit.icon, {
                        sx: { fontSize: 48 },
                      })}
                    </Box>
                    <Heading variant="h6" sx={{ mb: 2, fontSize: '1.1rem' }}>
                      {benefit.title}
                    </Heading>
                    <Text sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
                      {benefit.description}
                    </Text>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Requirements Section */}
        <Box sx={{ mb: 8 }}>
          <Heading
            variant="h2"
            sx={{ mb: 4, fontSize: '1.75rem', fontWeight: 600 }}
          >
            Service Areas We Cover
          </Heading>
          <Card
            sx={{
              maxWidth: '600px',
              mx: 'auto',
              position: 'relative',
              border: '1px solid var(--color-border)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -1,
                right: -1,
                width: '40px',
                height: '40px',
                borderTop: '3px solid var(--color-primary)',
                borderRight: '3px solid var(--color-primary)',
                borderRadius: '0 !important',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -1,
                left: -1,
                width: '40px',
                height: '40px',
                borderBottom: '3px solid var(--color-primary)',
                borderLeft: '3px solid var(--color-primary)',
                borderRadius: '0 !important',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <List>
                {serviceAreas.map((area, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={area}
                      primaryTypographyProps={{
                        sx: { fontSize: '0.95rem', lineHeight: 1.5 },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>

        {/* Process Section */}
        <Box sx={{ mb: 8 }}>
          <Heading
            variant="h2"
            sx={{ mb: 4, fontSize: '1.75rem', fontWeight: 600 }}
          >
            Partnership Process
          </Heading>
          <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
            {processSteps.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  sx={{ textAlign: 'center', maxWidth: '200px', mx: 'auto' }}
                >
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'var(--color-primary-text)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      fontWeight: 'bold',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    {step.step}
                  </Box>
                  <Heading variant="h6" sx={{ mb: 1, fontSize: '1rem' }}>
                    {step.title}
                  </Heading>
                  <Text
                    sx={{
                      fontSize: '0.85rem',
                      color: 'var(--color-text-muted)',
                      lineHeight: 1.4,
                    }}
                  >
                    {step.description}
                  </Text>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            backgroundColor:
              'color-mix(in srgb, var(--color-primary) 5%, transparent)',
            position: 'relative',
            border: '1px solid var(--color-border)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -1,
              left: -1,
              width: '40px',
              height: '40px',
              borderTop: '3px solid var(--color-primary)',
              borderLeft: '3px solid var(--color-primary)',
              borderRadius: '0 !important',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -1,
              right: -1,
              width: '40px',
              height: '40px',
              borderBottom: '3px solid var(--color-primary)',
              borderRight: '3px solid var(--color-primary)',
              borderRadius: '0 !important',
            },
          }}
        >
          <Heading
            variant="h2"
            sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
          >
            Ready to Start the Conversation?
          </Heading>
          <Text
            sx={{
              mb: 4,
              fontSize: '1.125rem',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            We're always interested in connecting with exceptional
            professionals. If you think you might be a fit for our curated
            collection of services, let's discuss the possibilities.
          </Text>
          <Button
            variant="contained"
            size="large"
            onClick={() =>
              window.open(
                'https://www.linkedin.com/company/rsm-collective/',
                '_blank',
                'noopener,noreferrer'
              )
            }
            aria-label="Contact RSM Collective on LinkedIn (opens in new window)"
            sx={{
              backgroundColor: 'primary.main',
              color: 'var(--color-primary-text)',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              borderTopLeftRadius: 0,
              borderTopRightRadius: '12px',
              borderBottomLeftRadius: '12px',
              borderBottomRightRadius: 0,
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            Start the Conversation
          </Button>
        </Box>
      </Container>
    </ContentPageLayout>
  );
}

export default JoinConsultant;
